import { KeepSelection } from "@/actions/selection/keep-selection"
import {
    ProductData,
    RelatedVariant
} from "@/lib/content-service/types/ContentServiceProduct"
import logger from "@/lib/logger/logger-init"
import { FREE_SHIPPING_VOUCHER_CODE } from "@/lib/siteconfig/constants"
import { Order } from "@/types/centra/checkout-api/payment/payment-reciept"
import {
    CheckoutAPIProduct,
    MinimizedCentraProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import { CheckoutAPISelectionWithRequiredToken } from "@/types/centra/checkout-api/selection/get-selection"
import { sendGTMEvent } from "@next/third-parties/google"
import { enrichTrackingDataWithCategories } from "./categoryEnrichment"
import { adaptProductForTracking, NormalizedTrackingProduct } from "./itemDataTransformer"
import {
    klaviyoAddedToCartEvent,
    klaviyoViewedProductEvent
} from "./klaviyoProductTracking"
import { extractProductHierarchy } from "./productHierarchyUtils"
import { storeProductOrigin } from "./productTrackingStore"
import { SiteContext } from "./siteContext"
import { getTrackingDataWithOrigin } from "./trackingDataWithOrigin"

// Safety net to prevent double tracking of the same transaction
const processedTransactionIds = new Set<string>()

// Get product ID safely based on product type
const getProductId = (product: any): string => {
    if ("items" in product && typeof product.items === "string") {
        if (product.items.includes("-")) {
            return product.items.slice(0, product.items.indexOf("-"))
        } else {
            if (product.items.length > 8) {
                // Remove last 4 characters (likely variant)
                let id = product.items.slice(0, -4)
                // If still more than 8, remove another 4 (could be size or another variant)
                if (id.length > 8) {
                    id = id.slice(0, -4)
                }
                return id
            } else {
                return product.items
            }
        }
    }

    if ("sku" in product) return product.sku
    if ("centraProduct" in product) return product.centraProduct
    if ("id" in product) return product.id
    return "unknown"
}

const clean = (obj: any): any => {
    if (obj === null || obj === undefined) {
        return undefined
    }

    if (typeof obj === "string") {
        return obj.trim() === "" ? undefined : obj
    }

    if (Array.isArray(obj)) {
        const cleaned = obj.map(clean).filter(item => item !== undefined)
        return cleaned.length === 0 ? undefined : cleaned
    }

    if (typeof obj === "object") {
        const cleaned: any = {}
        let hasValidProperties = false

        for (const [key, value] of Object.entries(obj)) {
            const cleanedValue = clean(value)
            if (cleanedValue !== undefined) {
                cleaned[key] = cleanedValue
                hasValidProperties = true
            }
        }

        return hasValidProperties ? cleaned : undefined
    }

    return obj
}

const send = (data: object) => {
    logger.debug("sendGTMEvent", data)
    const cleaned = clean(data)

    if (cleaned) {
        console.log("sendGTMEvent", cleaned)
        sendGTMEvent(cleaned)
    }
}

/**
 * Enriches tracking data with categories and returns the enriched data
 * This function can be used before calling the normal send function
 */
export async function enrichTrackingEventData(eventData: any): Promise<any> {
    if (!eventData?.ecommerce?.items) {
        return eventData
    }

    try {
        // Extract tracking items for enrichment
        const trackingItems = eventData.ecommerce.items
            .map((item: any) => ({
                item_id: item.item_id,
                item_variant: item.item_variant,
                item_category: item.item_category
            }))
            .filter((item: any) => item.item_id)

        if (trackingItems.length > 0) {
            // Get site context for API call
            const siteContext = {
                site_country: eventData.site_country || "",
                site_language: eventData.site_language || ""
            }

            // Enrich with categories
            const enrichedItems = await enrichTrackingDataWithCategories(
                trackingItems,
                siteContext
            )

            // Update the event data with enriched items
            const enrichedEventData = { ...eventData }
            enrichedEventData.ecommerce.items = enrichedEventData.ecommerce.items.map(
                (item: any, index: number) => ({
                    ...item,
                    ...enrichedItems[index]
                })
            )

            return enrichedEventData
        }
    } catch (error) {
        logger.error("Failed to enrich tracking event data with categories", error)
    }

    return eventData
}

export const setUserPropertiesEvent = (siteContext: SiteContext): boolean => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const eventData = {
        event: "set_user_properties",
        site_country,
        site_language
    }

    send(eventData)
    return true
}

export const viewItemEvent = async (
    product:
        | CheckoutAPIProduct
        | RelatedProduct
        | ProductData
        | MinimizedCentraProduct
        | RelatedVariant,
    item: any,
    siteContext: SiteContext,
    type: "Load PDP" | "Change Colour" | "Change Size" | "Quick Shop" = "Load PDP",
    listHierarchy?: string,
    promotionData?: { promotion_id?: string; promotion_name?: string }
): Promise<boolean> => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const itemData = getTrackingDataWithOrigin(product, {
        item_list_name: "Product Detail Page",
        item_list_id: "pdp",
        item_size: item?.size,
        promotion_id: promotionData?.promotion_id,
        promotion_name: promotionData?.promotion_name
    })

    const { currency, ...rest } = itemData
    const transformedItem = rest

    // Use the type parameter directly - no longer stored in session
    const eventType = type

    const eventData = {
        event: "view_item",
        site_country,
        site_language,
        type: eventType,
        list_hierarchy: listHierarchy || extractProductHierarchy(product),
        product_id: (() => {
            const id = getProductId(product)
            if (typeof id === "string" && id.length >= 9) {
                return id.slice(0, 7)
            }
            return id
        })(),
        product_name: product.name,
        ecommerce: {
            currency: transformedItem.currency || product.price?.slice(-3), // Use currency from transformed item, fallback to extracting from price
            value: product.priceAsNumber,
            items: [transformedItem]
        }
    }

    const enrichedEventData = await enrichTrackingEventData(eventData)
    send(enrichedEventData)

    // Only call klaviyoViewedProductEvent for compatible product types
    if ("sku" in product || "id" in product) {
        klaviyoViewedProductEvent(
            product as CheckoutAPIProduct | RelatedProduct | ProductData,
            item
        )
    }

    return true // We got through the event without errors
}

export const addToCartEvent = async (
    product: CheckoutAPIProduct | RelatedProduct | ProductData,
    item: any,
    siteContext: SiteContext,
    type: "pdp" | "cart" = "pdp",
    promotionData?: { promotion_id?: string; promotion_name?: string }
): Promise<boolean> => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const itemIndex = item?.index || 0

    // Use stored origin data if available, otherwise use current context
    const itemData = getTrackingDataWithOrigin(product, {
        item_list_name: type === "pdp" ? "Product Detail Page" : "Cart",
        item_list_id: type === "pdp" ? "pdp" : "cart",
        quantity: 1,
        index: itemIndex,
        promotion_id: promotionData?.promotion_id,
        promotion_name: promotionData?.promotion_name
    })

    const { currency, ...rest } = itemData
    const transformedItem = rest

    const eventData = {
        event: "add_to_cart",
        site_country,
        site_language,
        type,
        product_id: (() => {
            const id = getProductId(product)
            if (typeof id === "string" && id.length >= 9) {
                return id.slice(0, 7)
            }
            return id
        })(),
        product_name: product.name,
        ecommerce: {
            currency: transformedItem.currency || product.price?.slice(-3), // Use currency from transformed item, fallback to extracting from price
            value: product.priceAsNumber,
            items: [transformedItem]
        }
    }

    const enrichedEventData = await enrichTrackingEventData(eventData)
    send(enrichedEventData)
    klaviyoAddedToCartEvent(product, item)
    return true
}

export const beginCheckoutEvent = async (
    cart: KeepSelection | null,
    siteContext: SiteContext
): Promise<boolean> => {
    if (!cart) return false

    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const selection = cart?.checkoutApiSelection?.selection

    const items =
        selection?.items.map((item, index) => {
            const transformedItem = getTrackingDataWithOrigin(item.product, {
                item_list_name: "Checkout",
                item_list_id: "checkout",
                item_size: item.size,
                index,
                quantity: item.quantity
            })
            // Omit currency from each item, as it's now on the ecommerce object
            const { currency, ...rest } = transformedItem
            return rest
        }) || []

    const { grandTotalPriceAsNumber = 0, shippingPriceAsNumber = 0 } =
        selection?.totals ?? {}
    // Use subtotal (without shipping) as shipping cost is not known yet
    const value = grandTotalPriceAsNumber - shippingPriceAsNumber

    const eventData = {
        event: "begin_checkout",
        site_country,
        site_language,
        ecommerce: {
            currency: selection?.currency,
            value,
            items
        }
    }

    const enrichedEventData = await enrichTrackingEventData(eventData)
    send(enrichedEventData)
    return true
}

export const addShippingInfoEvent = async (
    cart: KeepSelection | null,
    siteContext: SiteContext
): Promise<boolean> => {
    if (!cart) return false

    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const selection = cart?.checkoutApiSelection?.selection

    const items =
        selection?.items.map((item, index) => {
            const transformedItem = getTrackingDataWithOrigin(item.product, {
                item_list_name: "Checkout",
                item_list_id: "checkout",
                item_size: item.size,
                index,
                quantity: item.quantity
            })
            // Omit currency from each item, as it's now on the ecommerce object
            const { currency, ...rest } = transformedItem
            return rest
        }) || []

    const coupon = selection?.discounts?.vouchers
        ?.map(voucher => voucher.voucher)
        .filter(voucherCode => voucherCode !== FREE_SHIPPING_VOUCHER_CODE)
        .join(", ")

    const { grandTotalPriceAsNumber = 0 } = selection?.totals ?? {}
    // Include shipping in the total price as user has selected shipping method
    const value = grandTotalPriceAsNumber
    const emailAddress = selection?.address.email || selection?.shippingAddress.email
    const firstName = selection?.address.firstName || selection?.shippingAddress.firstName
    const lastName = selection?.address.lastName || selection?.shippingAddress.lastName
    const country = selection?.shippingAddress?.country
    const city = selection?.shippingAddress?.city
    const postalCode = selection?.shippingAddress?.zipCode

    const eventData = {
        event: "add_shipping_info",
        site_country,
        site_language,
        ecommerce: {
            currency: selection?.currency,
            value,
            shipping_tier: selection?.shippingMethodName,
            coupon,
            items
        },
        user_data: {
            first_name: firstName,
            last_name: lastName,
            email: emailAddress,
            country,
            city,
            postal_code: postalCode
        }
    }

    const enrichedEventData = await enrichTrackingEventData(eventData)
    send(enrichedEventData)
    return true
}

export const addPaymentInfoEvent = async (
    data: CheckoutAPISelectionWithRequiredToken | Order | null | undefined,
    siteContext: SiteContext
): Promise<boolean> => {
    if (!data) return false

    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    let cartOrderData

    if ("selection" in data) {
        // is CheckoutAPISelectionWithRequiredToken
        cartOrderData = data?.selection
    } else {
        // is Order
        cartOrderData = data
    }

    const items =
        cartOrderData?.items.map((item, index) => {
            const transformedItem = getTrackingDataWithOrigin(item.product, {
                item_list_name: "Checkout",
                item_list_id: "checkout",
                item_size: item.size,
                index,
                quantity: item.quantity // This should be the individual item quantity
            })
            // Omit currency from each item, as it's now on the ecommerce object
            const { currency, ...rest } = transformedItem
            return rest
        }) || []

    const coupon = cartOrderData?.discounts?.vouchers
        ?.map(voucher => voucher.voucher)
        .filter(voucherCode => voucherCode !== FREE_SHIPPING_VOUCHER_CODE)
        .join(", ")

    const {
        grandTotalPriceAsNumber,
        grandTotalPriceTaxAsNumber: tax,
        shippingPriceAsNumber: shipping
    } = cartOrderData?.totals

    const value = grandTotalPriceAsNumber
    const emailAddress =
        cartOrderData.address.email || cartOrderData.shippingAddress.email
    const firstName =
        cartOrderData.address.firstName || cartOrderData.shippingAddress.firstName
    const lastName =
        cartOrderData.address.lastName || cartOrderData.shippingAddress.lastName
    const country = cartOrderData?.shippingAddress?.country
    const city = cartOrderData?.shippingAddress?.city
    const postalCode = cartOrderData?.shippingAddress?.zipCode

    const eventData = {
        event: "add_payment_info",
        site_country,
        site_language,
        ecommerce: {
            currency: cartOrderData?.currency,
            value,
            shipping_tier: cartOrderData?.shippingMethodName,
            payment_type: cartOrderData?.paymentMethodName || "Card",
            coupon,
            items
        },
        user_data: {
            first_name: firstName,
            last_name: lastName,
            email: emailAddress,
            country,
            city,
            postal_code: postalCode
        }
    }

    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)
    return true
}

export const purchaseEvent = async (
    order: Order | undefined,
    siteContext: SiteContext,
    locale?: string
): Promise<boolean> => {
    if (!order) return false

    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const transactionId = order?.order
    if (!transactionId) {
        logger.warn("Purchase event: No transaction ID found", { order })
        return false
    }

    // Safety net: Check if this transaction has already been processed
    if (processedTransactionIds.has(transactionId)) {
        logger.warn("Purchase event: Transaction already processed, skipping", {
            transactionId
        })
        return false
    }

    // Mark this transaction as processed
    processedTransactionIds.add(transactionId)

    const items =
        order?.items.map((item, index) => {
            const transformedItem = getTrackingDataWithOrigin(item.product, {
                item_list_name: "Purchase",
                item_list_id: "purchase",
                item_size: item.size,
                index,
                quantity: item.quantity
            })
            // Omit currency from each item, as it's now on the ecommerce object
            const { currency, ...rest } = transformedItem
            return rest
        }) || []

    const coupon = order?.discounts?.vouchers
        ?.map(voucher => voucher.voucher)
        .filter(voucherCode => voucherCode !== FREE_SHIPPING_VOUCHER_CODE)
        .join(", ")

    const {
        grandTotalPriceAsNumber,
        grandTotalPriceTaxAsNumber: tax,
        shippingPriceAsNumber: shipping
    } = order?.totals

    // Value should be the full amount customer pays, including shipping and tax
    const value = grandTotalPriceAsNumber
    const emailAddress = order.address.email || order.shippingAddress.email
    const firstName = order.address.firstName || order.shippingAddress.firstName
    const lastName = order.address.lastName || order.shippingAddress.lastName
    const country = order?.shippingAddress?.country
    const city = order?.shippingAddress?.city
    const postalCode = order?.shippingAddress?.zipCode

    const eventData = {
        event: "purchase",
        site_country,
        site_language,
        ecommerce: {
            currency: order?.currency,
            value,
            transaction_id: transactionId,
            shipping_tier: order?.shippingMethodName,
            payment_type: order?.paymentMethodName || "Card",
            shipping,
            tax,
            coupon,
            items
        },
        user_data: {
            first_name: firstName,
            last_name: lastName,
            email: emailAddress,
            country,
            city,
            postal_code: postalCode
        }
    }

    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)

    return true
}

export const viewItemListEvent = async (
    items: NormalizedTrackingProduct[],
    listName: string,
    listId: string,
    siteContext: SiteContext,
    type: "carousel" | "plp" | "plp_show_more" | "search" = "plp",
    listHierarchy?: string,
    listSortOrder?: string,
    searchTerm?: string,
    searchResults?: number
): Promise<boolean> => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const currency = items[0]?.currency || "EUR"
    const eventData: any = {
        event: "view_item_list",
        site_country,
        site_language,
        type,
        ecommerce: {
            currency: currency,
            items: items.map((item, index) => {
                // Omit currency from each item, as it's now on the ecommerce object
                const { currency, ...rest } = item
                return {
                    ...rest,
                    index
                }
            })
        }
    }

    // Add type-specific fields
    if (type === "carousel" || type === "plp" || type === "plp_show_more") {
        eventData.list_hierarchy = listHierarchy
        if (type === "plp" || type === "plp_show_more") {
            eventData.list_sort_order = listSortOrder
        }
    } else if (type === "search") {
        eventData.list_hierarchy = searchTerm
            ? `Search: ${searchTerm.toLowerCase()}`
            : undefined
        eventData.search_term = searchTerm?.toLowerCase()
        eventData.search_results = searchResults
    }

    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)
    return true
}

export const selectItemEvent = async (
    product:
        | CheckoutAPIProduct
        | RelatedProduct
        | ProductData
        | MinimizedCentraProduct
        | RelatedVariant,
    item: any,
    listName: string,
    listId: string,
    siteContext: SiteContext,
    type: string,
    listHierarchy?: string,
    promotionData?: { promotion_id?: string; promotion_name?: string },
    locale?: string
): Promise<boolean> => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const itemData = adaptProductForTracking(product, {
        item_list_name: listName,
        item_list_id: listId,
        item_size: item?.size,
        index: item?.index,
        quantity: 1,
        promotion_id: promotionData?.promotion_id,
        promotion_name: promotionData?.promotion_name
    })

    const { currency, ...rest } = itemData
    const transformedItem = rest

    // Store product origin for future tracking events
    const productKey = transformedItem.item_id

    // Determine discovery type based on the event type
    let discoveryType: "carousel" | "plp" | "search" | "quickshop" | "pdp" = "pdp"
    if (type === "carousel") discoveryType = "carousel"
    else if (type === "plp" || type === "plp_show_more") discoveryType = "plp"
    else if (type === "search") discoveryType = "search"
    else if (type === "quickshop") discoveryType = "quickshop"

    storeProductOrigin(
        productKey,
        transformedItem.item_list_id || "unknown",
        transformedItem.item_list_name || "Unknown",
        transformedItem.index,
        listHierarchy,
        promotionData?.promotion_id,
        promotionData?.promotion_name
    )

    const eventData = {
        event: "select_item",
        site_country,
        site_language,
        type,
        list_hierarchy: listHierarchy,
        product_id: (() => {
            const id = getProductId(product)
            if (typeof id === "string" && id.length >= 9) {
                return id.slice(0, 7)
            }
            return id
        })(),
        product_name: product.name,
        ecommerce: {
            currency: currency,
            value: product.priceAsNumber,
            items: [transformedItem]
        }
    }

    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)
    return true
}

export const viewPromotionEvent = async (
    promotionId: string,
    promotionName: string,
    creativeName: string,
    creativeSlot: string,
    siteContext: SiteContext,
    items?: NormalizedTrackingProduct[]
): Promise<boolean> => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const eventData = {
        event: "view_promotion",
        site_country,
        site_language,
        promotion_name: promotionName,
        ecommerce: {
            currency: items?.[0]?.currency || "EUR",
            items:
                items?.map((item, index) => {
                    // Omit currency from each item, as it's now on the ecommerce object
                    const { currency, ...rest } = item
                    return {
                        ...rest,
                        promotion_id: promotionId,
                        promotion_name: promotionName,
                        item_list_name: "promotion",
                        item_list_id: promotionName,
                        index
                    }
                }) || []
        }
    }

    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)
    return true
}

export const selectPromotionEvent = async (
    promotionId: string,
    promotionName: string,
    creativeName: string,
    creativeSlot: string,
    siteContext: SiteContext,
    items?: NormalizedTrackingProduct[]
): Promise<boolean> => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    // Store product origin for each item in the promotion
    if (items && items.length > 0) {
        items.forEach((item, index) => {
            const productKey = item.item_id

            // Store promotion origin for future tracking events
            storeProductOrigin(
                productKey,
                promotionName, // item_list_id
                "promotion", // item_list_name
                index,
                `Promotion: ${promotionName}`, // list_hierarchy
                promotionId, // promotion_id
                promotionName // promotion_name
            )
        })
    }

    const eventData = {
        event: "select_promotion",
        site_country,
        site_language,
        promotion_name: promotionName,
        ecommerce: {
            currency: items?.[0]?.currency || "EUR",
            items:
                items?.map((item, index) => {
                    // Omit currency from each item, as it's now on the ecommerce object
                    const { currency, ...rest } = item
                    return {
                        ...rest,
                        promotion_id: promotionId,
                        promotion_name: promotionName,
                        item_list_name: "promotion",
                        item_list_id: promotionName,
                        index
                    }
                }) || []
        }
    }

    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)
    return true
}

export const removeFromCartEvent = async (
    product: CheckoutAPIProduct | RelatedProduct | ProductData,
    item: any,
    quantity: number,
    siteContext: SiteContext,
    type: "pdp" | "cart" = "cart",
    promotionData?: { promotion_id?: string; promotion_name?: string },
    locale?: string
): Promise<boolean> => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    // Extract item data consistently
    const itemSize = item?.size || item?.name
    const itemIndex = item?.index || 0

    const itemData = getTrackingDataWithOrigin(product, {
        item_list_name: "Cart",
        item_list_id: "cart",
        item_size: itemSize,
        quantity: quantity,
        index: itemIndex,
        promotion_id: promotionData?.promotion_id,
        promotion_name: promotionData?.promotion_name
    })

    const { currency, ...rest } = itemData
    const transformedItem = rest

    const eventData = {
        event: "remove_from_cart",
        site_country,
        site_language,
        type,
        product_id: product.sku || (product as any).id,
        product_name: product.name,
        ecommerce: {
            currency: currency || product.price?.slice(-3), // Use currency from transformed item, fallback to extracting from price
            value: (product.priceAsNumber || 0) * quantity,
            items: [transformedItem]
        }
    }
    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)
    return true
}

export const viewCartEvent = async (
    cart: KeepSelection | null,
    siteContext: SiteContext,
    locale?: string
): Promise<boolean> => {
    if (!cart) {
        return false
    }

    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const selection = cart?.checkoutApiSelection?.selection

    const items =
        selection?.items.map((item, index) => {
            const transformedItem = getTrackingDataWithOrigin(item.product, {
                item_list_name: "Cart",
                item_list_id: "cart",
                item_size: item.size,
                index,
                quantity: item.quantity,
                promotion_id: undefined,
                promotion_name: undefined
            })
            // Omit currency from each item, as it's now on the ecommerce object
            const { currency, ...rest } = transformedItem
            return rest
        }) || []

    const { grandTotalPriceAsNumber = 0, shippingPriceAsNumber = 0 } =
        selection?.totals ?? {}
    const value = grandTotalPriceAsNumber - shippingPriceAsNumber

    const eventData = {
        event: "view_cart",
        site_country,
        site_language,
        ecommerce: {
            currency: selection?.currency,
            value,
            items
        }
    }

    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)
    return true
}

export const filterEvent = async (
    filterType: string,
    filterValue: string,
    siteContext: SiteContext,
    listHierarchy?: string,
    listSortOrder?: string,
    filterResults?: number
): Promise<boolean> => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const eventData = {
        event: "plp_apply_filter",
        site_country,
        site_language,
        type: "plp",
        list_hierarchy: listHierarchy,
        list_sort_order: listSortOrder,
        list_filter_type: filterType,
        list_filter_value: filterValue,
        list_filter_results: filterResults
    }
    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)
    return true
}

export const sortEvent = async (
    sortType: string,
    sortValue: string,
    siteContext: SiteContext,
    listHierarchy?: string
): Promise<boolean> => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const eventData = {
        event: "plp_change_sort_order",
        site_country,
        site_language,
        type: "plp",
        list_hierarchy: listHierarchy,
        list_sort_order: sortValue
    }

    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)
    return true
}

export const signUpEvent = (
    method: string,
    siteContext: SiteContext,
    userData?: {
        first_name?: string
        last_name?: string
        email?: string
        country?: string
        city?: string
        postal_code?: string
    }
): boolean => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const eventData: any = {
        event: "sign_up",
        site_country,
        site_language,
        type: method
    }

    if (userData) {
        eventData.user_data = {
            first_name: userData.first_name,
            last_name: userData.last_name,
            email: userData.email,
            country: userData.country,
            city: userData.city,
            postal_code: userData.postal_code
        }
    }

    send(eventData)
    return true
}

export const accordionEvent = async (
    accordionName: string,
    siteContext: SiteContext,
    type?: string,
    productSku?: string,
    productName?: string
): Promise<boolean> => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const eventData: any = {
        event: "accordion_interaction",
        site_country,
        site_language,
        type: type || "PDP",
        click_path: accordionName
    }

    if (productSku && productName) {
        eventData.product_id = productSku
        eventData.product_name = productName
    }

    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)
    return true
}

export const modalEvent = (
    modalId: string,
    modalName: string,
    action: "open" | "close",
    siteContext: SiteContext
): boolean => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const eventName = action === "open" ? "modal_open" : "modal_close"

    const eventData = {
        event: eventName,
        site_country,
        site_language,
        modal_id: modalId,
        modal_name: modalName
    }

    send(eventData)
    return true
}

export const navigationEvent = (
    navigationName: string,
    navigationType: string,
    siteContext: SiteContext,
    clickPath?: string
): boolean => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const eventName = navigationType === "footer" ? "click_footer" : "click_top_nav"

    const eventData = {
        event: eventName,
        site_country,
        site_language,
        click_path: clickPath || navigationName
    }

    send(eventData)
    return true
}

export const checkoutAddVoucherEvent = (
    voucherCode: string,
    voucherStatus: "valid" | "invalid",
    siteContext: SiteContext
): boolean => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const eventData = {
        event: "checkout_add_voucher",
        site_country,
        site_language,
        voucher_status: voucherStatus,
        ecommerce: {
            coupon: voucherCode.toLowerCase()
        }
    }

    send(eventData)
    return true
}

export const shippingPageEvent = async (
    cart: KeepSelection | null,
    siteContext: SiteContext,
    voucherCode?: string
): Promise<boolean> => {
    if (!cart) return false

    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const selection = cart?.checkoutApiSelection?.selection

    const items =
        selection?.items.map((item, index) => {
            const transformedItem = getTrackingDataWithOrigin(item.product, {
                item_list_name: "Checkout",
                item_list_id: "checkout",
                item_size: item.size,
                index,
                quantity: item.quantity
            })
            // Omit currency from each item, as it's now on the ecommerce object
            const { currency, ...rest } = transformedItem
            return rest
        }) || []

    const { grandTotalPriceAsNumber = 0 } = selection?.totals ?? {}
    // Include shipping in the total price as user sees total with shipping on this page
    const value = grandTotalPriceAsNumber

    const eventData: any = {
        event: "shipping_page",
        site_country,
        site_language,
        ecommerce: {
            currency: selection?.currency,
            value,
            items
        }
    }

    if (voucherCode) {
        eventData.ecommerce.coupon = voucherCode.toLowerCase()
    }

    const finalizedData = await enrichTrackingEventData(eventData)
    send(finalizedData)
    return true
}

export const pdpChangeSkuEvent = (
    type: "change_colour" | "change_size",
    productId: string,
    productName: string,
    skuSelected: string,
    siteContext: SiteContext
): boolean => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const baseProductId = (() => {
        if (typeof productId === "string" && productId.length >= 7) {
            return productId.slice(0, 7)
        }
        return "unknown"
    })()

    if (type === "change_size") {
        skuSelected = skuSelected.slice(-4)
    }

    const eventData = {
        event: "pdp_change_sku",
        site_country,
        site_language,
        type,
        product_id: baseProductId,
        product_name: productName,
        sku_selected: skuSelected
    }

    send(eventData)
    return true
}

export const pdpSizeGuideEvent = (
    productId: string,
    productName: string,
    siteContext: SiteContext
): boolean => {
    const { site_country, site_language } = siteContext

    if (!site_country || !site_language) {
        return false
    }

    const eventData = {
        event: "pdp_size_guide",
        site_country,
        site_language,
        product_id: productId,
        product_name: productName
    }

    send(eventData)
    return true
}
